import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { professionalsApi } from "../../../services/professionalsApi";
import type { Professional } from "../../../services/professionalsApi";
import {
  availabilityApi,
  AvailabilityData,
} from "../../../services/availabilityManagementApi";
import { useToaster } from "../../shared/Toaster";
import { Icons } from "../../shared/Icons";
import { Availability } from "../AdminSettings/Availability";
import { AdminPageHeaderWithBack } from "../shared";

export const ProfessionalAvailabilityPage: React.FC = () => {
  const { professionalId } = useParams<{
    professionalId: string;
  }>();
  const navigate = useNavigate();
  const { showToast } = useToaster();

  const [professional, setProfessional] = useState<Professional | null>(null);
  const [availability, setAvailability] = useState<AvailabilityData>({
    monday: { open: false, intervals: [] },
    tuesday: { open: false, intervals: [] },
    wednesday: { open: false, intervals: [] },
    thursday: { open: false, intervals: [] },
    friday: { open: false, intervals: [] },
    saturday: { open: false, intervals: [] },
    sunday: { open: false, intervals: [] },
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const currentProfessionalId = professionalId
    ? parseInt(professionalId, 10)
    : 0;

  const fetchProfessional = useCallback(async () => {
    try {
      const response = await professionalsApi.getProfessionalById(
        currentProfessionalId
      );
      if (response.success && response.data) {
        setProfessional(response.data);
      } else {
        showToast("error", response.error || "Falha ao carregar profissional");
        navigate(-1);
      }
    } catch (err) {
      showToast("error", "Falha ao carregar profissional");
      navigate(-1);
    }
  }, [currentProfessionalId, showToast, navigate]);

  const fetchAvailability = useCallback(async () => {
    setLoading(true);
    try {
      const response = await availabilityApi.getAvailabilityByProfessional(
        currentProfessionalId
      );
      if (response.success && response.data) {
        setAvailability(response.data);
      } else {
        showToast(
          "error",
          response.error || "Falha ao carregar disponibilidade"
        );
      }
    } catch (err) {
      showToast("error", "Falha ao carregar disponibilidade");
    } finally {
      setLoading(false);
    }
  }, [currentProfessionalId, showToast]);

  useEffect(() => {
    if (currentProfessionalId) {
      fetchProfessional();
      fetchAvailability();
    }
  }, [currentProfessionalId, fetchProfessional, fetchAvailability]);

  const handleSaveAvailability = async () => {
    if (!professional) return;

    setSaving(true);
    try {
      const response = await availabilityApi.updateAvailabilityByProfessional(
        currentProfessionalId,
        availability
      );
      if (response.success) {
        await fetchAvailability();
      } else {
        showToast("error", response.error || "Falha ao salvar disponibilidade");
      }
    } catch (err) {
      showToast("error", "Falha ao salvar disponibilidade");
    } finally {
      setSaving(false);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-fade-in">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-12 text-center">
          <div className="flex flex-col items-center gap-4">
            <Icons.Refresh sx={{ fontSize: 48 }} className="animate-spin text-gray-400 dark:text-slate-500" />
            <p className="text-xl font-medium text-gray-600 dark:text-slate-300">
              Carregando disponibilidade...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <AdminPageHeaderWithBack
        icon={<Icons.Calendar sx={{ fontSize: 28 }} />}
        title="Gerenciar Disponibilidade"
        subtitle={
          professional ? `Profissional: ${professional.name}` : "Carregando..."
        }
        onBackClick={handleGoBack}
        buttonText={saving ? "Salvando..." : "Salvar Disponibilidade"}
        onButtonClick={handleSaveAvailability}
        buttonDisabled={saving || !professional}
        buttonLoading={saving}
        buttonVariant="success"
      />

      {/* Availability Content */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-6">
        {professional && (
          <Availability
            availability={availability}
            professionalName={professional.name}
            setAvailability={setAvailability}
            onSave={handleSaveAvailability}
          />
        )}
      </div>
    </div>
  );
};
